<template>
  <div
    itemscope
    itemprop="mainEntity"
    itemtype="https://schema.org/Question"
    class="rounded-3xl bg-white p-6 md:p-8"
  >
    <button
      class="group flex w-full items-start justify-between gap-6 text-pretty rounded-none p-0 text-left text-lg font-medium leading-tight transition-colors duration-300 ease-out-sine hover:text-blue-500 focus:outline-none md:text-xl"
      @click="isExpanded = !isExpanded"
    >
      <span itemprop="name text">
        {{ item.question }}
      </span>

      <figure
        class="relative h-8 w-8 shrink-0 rounded-lg bg-gray-50 stroke-2 p-1 transition-bg duration-300 ease-out-sine group-hover:bg-blue-50 md:-my-2 md:-mr-2 md:h-10 md:w-10 md:rounded-xl md:p-2"
      >
        <PlusIcon
          class="absolute left-1.5 top-1.5 h-5 w-5 stroke-2 transition-cheap md:left-2 md:top-2 md:h-6 md:w-6"
          :class="{
            '-rotate-90 opacity-0': isExpanded,
          }"
        />
        <MinusIcon
          class="absolute left-1.5 top-1.5 h-5 w-5 stroke-2 transition-cheap md:left-2 md:top-2 md:h-6 md:w-6"
          :class="{
            'rotate-90 opacity-0': !isExpanded,
          }"
        />
      </figure>
    </button>

    <div
      ref="contentRef"
      itemscope
      itemprop="acceptedAnswer"
      itemtype="https://schema.org/Answer"
      class="text-base md:text-lg"
      :class="{
        'pointer-events-none': !isExpanded,
      }"
    >
      <div
        itemprop="text"
        class="c-faq-content max-w-3xl text-pretty pt-6 leading-normal"
        v-html="item.answer"
      ></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, ref, useTemplateRef, watch } from 'vue'
import { useEventListener, useDebounceFn } from '@vueuse/core'
import { MinusIcon, PlusIcon } from '@heroicons/vue/24/outline'

import { isClient } from '/@shared/app'

defineProps<{
  item: {
    question: string
    answer: string
  }
}>()

const isExpanded = ref(false)
const contentRef = useTemplateRef('contentRef')

if (isClient) {
  const stop = watch(
    contentRef,
    async val => {
      if (!val) return

      val.style.height = '0px'
      val.style.opacity = '0'

      await nextTick()
      stop()
    },
    { immediate: true }
  )

  const debouncedUpdateHeight = useDebounceFn(() => {
    if (!isExpanded.value) return

    animateToggleCollapse()
  }, 250)
  useEventListener(window, 'resize', debouncedUpdateHeight)
  useEventListener(window, 'orientationchange', debouncedUpdateHeight)
}

async function animateToggleCollapse() {
  if (!contentRef.value) return

  const animation = contentRef.value.animate(
    [
      {
        height:
          (isExpanded.value
            ? contentRef.value.children[0].clientHeight || 0
            : 0) + 'px',
        opacity: isExpanded.value ? 1 : 0,
      },
    ],
    {
      duration: 400,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // easeOutBack
      fill: 'forwards',
    }
  )

  await animation.finished

  animation.commitStyles()
  animation.cancel()
}
watch(isExpanded, () => {
  animateToggleCollapse()
})
</script>
